import React, { Component, useState, useEffect, useCallback, useRef } from 'react';
import { GripVertical, RotateCcw } from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';

/**
 * DraggableTabBar — reusable tab bar with drag-to-reorder and persistence.
 *
 * Props:
 *   tabs            – array of { id, label, icon } (default order)
 *   activeTab       – currently selected tab id
 *   onTabChange     – callback(tabId) when a tab is clicked
 *   prefKey         – DB key for persisting order (e.g. 'crm_tab_order')
 *   className       – CSS class for the container
 *   tabClassName    – CSS class for each tab button
 *   activeClassName – CSS class appended when tab is active
 *   style           – inline style for the container
 *   tabStyle        – function(tabId) => style object for each tab
 *   iconSize        – icon pixel size (default 15)
 */

// ── Error boundary — crash falls back to plain tabs ─────────────────────────
class TabBarErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('[DraggableTabBar] Render error:', err); }
  render() {
    if (this.state.hasError) {
      const { tabs, activeTab, onTabChange, className, tabClassName, activeClassName, style, tabStyle, iconSize } = this.props;
      return (
        <div className={className} style={style} role="tablist">
          {(Array.isArray(tabs) ? tabs : []).map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button key={t.id} className={`${tabClassName || ''} ${isActive ? activeClassName || '' : ''}`}
                onClick={() => onTabChange(t.id)} role="tab"
                style={typeof tabStyle === 'function' ? tabStyle(t.id) : {}}>
                {Icon && <Icon size={iconSize || 15} />} <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Main component ──────────────────────────────────────────────────────────
function DraggableTabBarInner({
  tabs: defaultTabs,
  activeTab,
  onTabChange,
  prefKey,
  className = '',
  tabClassName = '',
  activeClassName = '',
  style,
  tabStyle,
  iconSize = 15,
}) {
  const [orderedTabs, setOrderedTabs] = useState(defaultTabs);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [isCustomized, setIsCustomized] = useState(false);
  const tabsRef = useRef(null);
  const { onKeyDown: handleKeyDown } = useArrowNav(tabsRef, 'button[role="tab"]', { vertical: false, activateOnFocus: true });

  // Load saved order on mount
  useEffect(() => {
    if (!prefKey) return;
    let cancelled = false;
    (async () => {
      try {
        if (!window.zignagenixAPI?.loadUiPreference) return;
        const saved = await window.zignagenixAPI.loadUiPreference(prefKey);
        if (cancelled) return;
        if (saved && Array.isArray(saved) && saved.length > 0) {
          const idMap = {};
          defaultTabs.forEach(t => { idMap[t.id] = t; });
          const usedIds = new Set();
          const reordered = saved
            .filter(id => idMap[id] && !usedIds.has(id))
            .map(id => { usedIds.add(id); return idMap[id]; });
          // Append any new tabs not in saved order
          defaultTabs.forEach(t => { if (!usedIds.has(t.id)) reordered.push(t); });
          if (reordered.length > 0) {
            setOrderedTabs(reordered);
            setIsCustomized(true);
          }
        }
      } catch (err) {
        console.warn('[DraggableTabBar] Load prefs failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [prefKey]); // eslint-disable-line

  // Persist order
  const saveOrder = useCallback(async (tabs) => {
    setOrderedTabs(tabs);
    setIsCustomized(true);
    if (!prefKey || !window.zignagenixAPI?.saveUiPreference) return;
    try {
      await window.zignagenixAPI.saveUiPreference(prefKey, tabs.map(t => t.id));
    } catch (err) {
      console.warn('[DraggableTabBar] Save failed:', err);
    }
  }, [prefKey]);

  // Reset to defaults
  const resetOrder = useCallback(async () => {
    setOrderedTabs(defaultTabs);
    setIsCustomized(false);
    if (!prefKey || !window.zignagenixAPI?.clearUiPreference) return;
    try {
      await window.zignagenixAPI.clearUiPreference(prefKey);
    } catch (err) { /* ignore */ }
  }, [defaultTabs, prefKey]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
    // Reduce opacity of the dragged element
    requestAnimationFrame(() => {
      if (e.target) e.target.style.opacity = '0.35';
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    if (e.target) e.target.style.opacity = '1';
    setDragIdx(null);
    setOverIdx(null);
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragIdx) setOverIdx(idx);
  }, [dragIdx]);

  const handleDrop = useCallback((e, toIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === toIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const newTabs = [...orderedTabs];
    const [moved] = newTabs.splice(dragIdx, 1);
    newTabs.splice(toIdx > dragIdx ? toIdx : toIdx, 0, moved);
    saveOrder(newTabs);
    setDragIdx(null);
    setOverIdx(null);
  }, [dragIdx, orderedTabs, saveOrder]);

  if (!Array.isArray(orderedTabs) || orderedTabs.length === 0) return null;

  return (
    <div
      ref={tabsRef}
      className={className}
      style={style}
      onKeyDown={handleKeyDown}
      role="tablist"
    >
      {orderedTabs.map((t, idx) => {
        if (!t || !t.id) return null;
        const Icon = t.icon;
        const isActive = activeTab === t.id;
        const isOver = overIdx === idx && dragIdx !== null && dragIdx !== idx;
        const computedStyle = typeof tabStyle === 'function' ? tabStyle(t.id) : {};

        return (
          <button
            key={t.id}
            className={`${tabClassName} ${isActive ? activeClassName : ''}`}
            onClick={() => onTabChange(t.id)}
            role="tab"
            aria-selected={isActive}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={(e) => handleDrop(e, idx)}
            style={{
              ...computedStyle,
              borderLeft: isOver ? '2.5px solid #4fc3f7' : undefined,
              cursor: 'grab',
            }}
          >
            <span style={{ opacity: 0.25, marginRight: 2, display: 'inline-flex', alignItems: 'center', flexShrink: 0, pointerEvents: 'none' }}>
              <GripVertical size={10} />
            </span>
            {Icon && <Icon size={iconSize} />}
            <span>{t.label}</span>
          </button>
        );
      })}

      {isCustomized && (
        <button
          onClick={resetOrder}
          title="Reset tab order to default"
          style={{
            background: 'none', border: 'none', color: '#78909c', cursor: 'pointer',
            padding: '4px 6px', marginLeft: 4, borderRadius: 4, display: 'flex',
            alignItems: 'center', gap: 2, fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          <RotateCcw size={12} />
        </button>
      )}
    </div>
  );
}

// ── Wrapped export ──────────────────────────────────────────────────────────
export default function DraggableTabBar(props) {
  return (
    <TabBarErrorBoundary {...props}>
      <DraggableTabBarInner {...props} />
    </TabBarErrorBoundary>
  );
}
